import { useState, useEffect, createContext, useContext } from "react";

interface ChangelogEntry {
  date: string;
  title: string;
  description?: string;
  pr?: number;
}

interface ChangelogData {
  entries: ChangelogEntry[];
}

const LAST_SEEN_KEY = "rockrecord_changelog_last_seen";

interface AppToolbarContextValue {
  open: () => void;
  hasNew: boolean;
  version: string;
  ready: boolean;
}

const AppToolbarContext = createContext<AppToolbarContextValue>({
  open: () => {},
  hasNew: false,
  version: "",
  ready: false,
});

export function useAppToolbar() {
  return useContext(AppToolbarContext);
}

/**
 * Provider that fetches changelog/version data and renders the full-screen
 * panel when opened. Children can use `useAppToolbar()` to get `open()` and `hasNew`.
 */
export function AppToolbarProvider({ children }: { children: React.ReactNode }) {
  const [changelog, setChangelog] = useState<ChangelogData | null>(null);
  const [version, setVersion] = useState<string>("");
  const [isOpen, setIsOpen] = useState(false);
  const [hasNew, setHasNew] = useState(false);

  useEffect(() => {
    Promise.all([
      fetch("/api/changelog").then((r) => r.json()),
      fetch("/api/version").then((r) => r.json()),
    ]).then(([cl, ver]) => {
      setChangelog(cl);
      setVersion(ver.version);

      const lastSeen = localStorage.getItem(LAST_SEEN_KEY);
      const latestDate = cl.entries?.[0]?.date;
      if (latestDate && latestDate !== lastSeen) {
        setHasNew(true);
      }
    }).catch(() => {});
  }, []);

  const dismiss = () => {
    setIsOpen(false);
    setHasNew(false);
    if (changelog?.entries?.[0]?.date) {
      localStorage.setItem(LAST_SEEN_KEY, changelog.entries[0].date);
    }
  };

  const ctx: AppToolbarContextValue = {
    open: () => setIsOpen(true),
    hasNew,
    version,
    ready: !!changelog,
  };

  return (
    <AppToolbarContext.Provider value={ctx}>
      {children}

      {/* Full-screen panel */}
      {isOpen && changelog && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 10001,
            background: "#12121e",
            color: "#e0e0e0",
            fontFamily: "system-ui, sans-serif",
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Top bar */}
          <div style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "16px 20px",
            borderBottom: "1px solid #2a2a3e",
            flexShrink: 0,
          }}>
            <div>
              <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>RockRecord</h1>
              <span style={{ fontSize: 12, color: "#666" }}>v{version}</span>
            </div>
            <button
              onClick={dismiss}
              style={{
                background: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: 8,
                color: "#ccc",
                cursor: "pointer",
                width: 36,
                height: 36,
                display: "grid",
                placeItems: "center",
                fontSize: 18,
              }}
              title="Close"
            >
              &times;
            </button>
          </div>

          {/* Content */}
          <div style={{ padding: "24px 20px", maxWidth: 600, width: "100%" }}>
            <h2 style={{ margin: "0 0 16px", fontSize: 15, fontWeight: 600, color: "#aaa", textTransform: "uppercase", letterSpacing: "0.05em" }}>
              What's New
            </h2>

            {Object.entries(
              changelog.entries.reduce<Record<string, ChangelogEntry[]>>((groups, entry) => {
                (groups[entry.date] ??= []).push(entry);
                return groups;
              }, {})
            ).map(([date, entries]) => (
              <div key={date} style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 11, color: "#555", marginBottom: 8, borderBottom: "1px solid #222", paddingBottom: 4 }}>{date}</div>
                {entries.map((entry, i) => (
                  <div key={i} style={{ marginBottom: 12, paddingLeft: 12 }}>
                    <strong style={{ fontSize: 14 }}>{entry.title}</strong>
                    {entry.pr && <span style={{ fontSize: 11, color: "#555" }}> #{entry.pr}</span>}
                    {entry.description && (
                      <p style={{ margin: "4px 0 0", fontSize: 13, color: "#999", lineHeight: 1.5 }}>{entry.description}</p>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </AppToolbarContext.Provider>
  );
}

/**
 * Gear icon button — use this wherever you want to place the trigger.
 * Renders as a 40x40 button matching ViewToolbarButton styling.
 */
export function AppToolbarButton({ style }: { style?: React.CSSProperties }) {
  const { open, hasNew, ready } = useAppToolbar();
  if (!ready) return null;

  return (
    <button
      onClick={open}
      title="Settings & info"
      style={{
        width: 40,
        height: 40,
        borderRadius: 12,
        border: "1px solid rgba(255,255,255,0.18)",
        background: "rgba(15,23,38,0.55)",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        boxShadow: "0 6px 18px rgba(0,0,0,0.35)",
        color: "#f7f8fb",
        cursor: "pointer",
        display: "grid",
        placeItems: "center",
        padding: 0,
        position: "relative",
        ...style,
      }}
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1.08-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1.08 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1.08 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1.08z" />
      </svg>
      {hasNew && (
        <span style={{
          position: "absolute",
          top: -2,
          right: -2,
          width: 8,
          height: 8,
          borderRadius: "50%",
          background: "#4fc3f7",
          border: "2px solid rgba(15,23,38,0.55)",
          boxShadow: "0 0 6px rgba(79,195,247,0.6)",
        }} />
      )}
    </button>
  );
}
